import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BackButton from "../../components/BackButton";
import { allowedViews, pathHome, pathLeagueView, type View } from "../../../config/routes";
import {
  AdPositionEnum,
  LanguageLocaleEnum,
  League,
  PlayerStatisticsListV2,
  StatisticKeyEnum
} from "../../../client/api";
import { showTranslated, translate } from "../../../config/translator";
import Loading from "../../components/Loading";
import RoundedRectangle from "../../components/RoundedRectangle";
import TabletLayoutPage, { TabletContainer } from "../../components/Layout/TabletLayout";
import { MainColumn, RightRail, TwoColumnGrid } from "../../components/Layout/TwoColumn";
import SegmentedTabs from "../../components/SegmentedTabs";
import Table from "../../components/Table";
import { AdSlot } from "../../components/AdSlot";
import { fetchLeagueById } from "../../../store/actions/leagueActions";
import { AppDispatch, RootState } from "../../../store";
import LeagueSelector from "../../components/LeagueSelector";
import LeagueStatisticsPlayer from "./LeagueStatisticsPlayer";
import StatisticsPanels from "./LeagueStatisticsPlayer/StatisticsPanels";
import PlayerStatisticPanel, { PlayerStatData } from "./LeagueStatisticsPlayer/PlayerStatisticPanel";
import { isPremium } from "../../../config/utils";
import LeagueMatchesView from "./LeagueMatchesView";
import LeagueTotwView from "./LeagueTotwView";
import LeagueStatsView from "./LeagueStatsView";
import { ClavaContext } from "../../../config/contexts";
import { addFavorite, removeFavorite } from "../../../store/actions/userActions";

const LeaguePageWrapper: React.FC = () => {
  const { leagueId: leagueIdParam, view: viewParam } = useParams<{
    leagueId?: string;
    view?: string;
  }>();

  const leagueId = useMemo(() => {
    const n = leagueIdParam ? parseInt(leagueIdParam, 10) : NaN;
    return Number.isFinite(n) ? n : undefined;
  }, [leagueIdParam]);
  const view: View | undefined = useMemo(
    () => (viewParam && allowedViews.includes(viewParam as View) ? (viewParam as View) : undefined),
    [viewParam]
  );
  const dispatch = useDispatch<AppDispatch>();
  const leagues = useSelector<RootState, RootState["leagues"]["value"]>(
    (s) => s.leagues.value
  );
  const league = useMemo(() => leagues.find((x) => x.id === leagueId), [leagueId, leagues]);
  useEffect(() => {
    if (!league && leagueId) {
      fetchLeagueById(dispatch, leagueId);
    }
  }, [leagueId, leagues, dispatch, league]);
  if (!league) {
    return <Loading />;
  }

  if (!leagueId) {
    return <Navigate to={pathHome()} replace />;
  }
  return <LeaguePage league={league} allLeagues={leagues} view={view} />;
};
const LeaguePage: React.FC<{ league: League, allLeagues: League[], view: View | undefined }> = ({
                                                                                                  league,
                                                                                                  view,
                                                                                                  allLeagues
                                                                                                }) => {
  const { l, user, pro } = useContext(ClavaContext);
  const dispatch = useDispatch<AppDispatch>();
  const favorites = useSelector<RootState, RootState["user"]["favorites"]>((state) => state.user.favorites);
  const isFollowing = useMemo(
    () => favorites.some((fav) => fav.type === "league" && fav.id === league.id),
    [favorites, league.id]
  );
  const isCup = useMemo(() => league.subCategory.isCup, [league.subCategory.isCup]);
  const handleFollowToggle = useCallback(() => {
    if (isFollowing) {
      removeFavorite(dispatch, { id: league.id, type: "league" });
    } else {
      addFavorite(dispatch, { id: league.id, type: "league" });
    }
  }, [league.id, isFollowing, dispatch]);
  const navigate = useNavigate();


  const subCategoryId = league.subCategory.id;
  const cupLeagues = useMemo(() => {
    if (!isCup || !subCategoryId) return [];
    return allLeagues.filter(
      (leagueItem) => leagueItem.subCategory.id === subCategoryId && leagueItem.active
    );
  }, [isCup, subCategoryId, allLeagues]);

  const leagueSelectorItems = useMemo(
    () => cupLeagues.map((leagueItem) => ({
      key: leagueItem.id,
      label: showTranslated(leagueItem.name, l)
    })),
    [cupLeagues, l]
  );

  const leagueSelectorItemsWithAll = useMemo(
    () => [
      { key: "all" as const, label: translate("allGroups", l) },
      ...cupLeagues.map((leagueItem) => ({
        key: leagueItem.id,
        label: showTranslated(leagueItem.name, l)
      }))
    ],
    [cupLeagues, l]
  );

  const handleLeagueChange = useCallback((newLeagueId: number | string | undefined) => {
    if (typeof newLeagueId === "number" && newLeagueId !== league.id) {
      navigate(pathLeagueView(newLeagueId, view || "table"));
    }
  }, [league.id, navigate, view]);


  const [playerStatsData, setPlayerStatsData] = useState<PlayerStatisticsListV2 | null>(null);
  const [premiumUserKey, setPremiumUserKey] = useState(0);
  const [dataUpdateKey, setDataUpdateKey] = useState(0);

  const isPremiumUser = useMemo(() => isPremium(user), [user]);

  const handlePlayerStatsLoaded = useCallback((data: PlayerStatisticsListV2) => {
    setPlayerStatsData(data);
    setDataUpdateKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    setPremiumUserKey((prev) => prev + 1);
  }, [isPremiumUser]);

  const extractStatisticData = useCallback((
    statisticKey: StatisticKeyEnum,
    limit = 3
  ): PlayerStatData[] => {
    if (!playerStatsData) return [];
    const stat = playerStatsData.find((s) => s.statistic.key === statisticKey);
    if (!stat) return [];
    return stat.playerStatistics.slice(0, limit).map(({ player, value, placement }) => ({
      player,
      value,
      placement
    }));
  }, [playerStatsData]);


  const evaluationData = useMemo(
    () => extractStatisticData(StatisticKeyEnum.PLAYER_RATING),
    [extractStatisticData]
  );

  const topScorerData = useMemo(
    () => extractStatisticData(StatisticKeyEnum.PLAYER_MOST_GOALS),
    [extractStatisticData]
  );

  const mostAssistData = useMemo(
    () => extractStatisticData(StatisticKeyEnum.PLAYER_MOST_ASSISTS),
    [extractStatisticData]
  );

  const mostMinutesData = useMemo(
    () => extractStatisticData(StatisticKeyEnum.PLAYER_MOST_MINUTES),
    [extractStatisticData]
  );

  const segments = useMemo(() => [
    {
      key: "table",
      label: translate("table", l),
      to: league.id ? pathLeagueView(league.id, "table") : "#"
    },
    {
      key: "matches",
      label: translate("matches", l),
      to: league.id ? pathLeagueView(league.id, "matches") : "#"
    },
    ...(isCup ? [] : [{
      key: "totw",
      label: "TOTW",
      to: league.id ? pathLeagueView(league.id, "totw") : "#"
    }]),
    {
      key: "teamstats",
      label: "Team Stats",
      to: league.id ? pathLeagueView(league.id, "teamstats") : "#"
    }
  ], [l, league.id, isCup]);

  if (!view) {
    return <Navigate to={pathLeagueView(league.id, "table")} replace />;
  }

  const isTableViewVisible = !view || view === "table";
  const isMatchesViewVisible = view === "matches";
  const isTotwViewVisible = !isCup && view === "totw";
  const isTeamStatsVisible = view === "teamstats";
  return (
    <TabletLayoutPage>
      <TabletContainer className="LeaguePage">
        <TwoColumnGrid>
          <MainColumn>
            <div className="flex flex-col relative pt-0">
              <LeagueHeader
                league={league}
                isCup={isCup}
                l={l}
                onBack={() => navigate(-1)}
                canFollow
                isFollowing={isFollowing}
                onFollowToggle={handleFollowToggle}
              />

              <div className="block xl:hidden space-y-6 mb-6">
                {(evaluationData.length > 0 || !pro) && (
                  <PlayerStatisticPanel
                    titleKey="evaluation"
                    players={evaluationData}
                    formatAsDecimal
                    shouldBlur={!pro}
                  />
                )}
                {topScorerData.length > 0 && (
                  <PlayerStatisticPanel
                    titleKey="topScorerTitle"
                    players={topScorerData}
                  />
                )}
              </div>

              <SegmentedTabs
                className="justify-start w-full pt-3 pb-6 mt-1 xl:pt-2 xl:pb-4 xl:mt-0"
                activeKey={view ?? "table"}
                replace
                items={segments}
              />

              <LeagueTableView
                isVisible={isTableViewVisible}
                isCup={isCup}
                leagueSelectorItems={leagueSelectorItems}
                leagueId={league.id}
                onLeagueChange={handleLeagueChange}
                l={l}
              />

              <LeagueMatchesView
                isVisible={isMatchesViewVisible}
                isCup={isCup}
                leagueSelectorItemsWithAll={leagueSelectorItemsWithAll}
                leagueId={league.id}
                subCategoryId={subCategoryId}
              />

              <LeagueTotwView
                isVisible={isTotwViewVisible}
                hasTotwRestriction={!pro}
                league={league}
              />

              <LeagueStatsView
                isVisible={isTeamStatsVisible}
                l={l}
              />

              <div className="block xl:hidden space-y-6 mb-6">
                {(mostAssistData.length > 0 || !pro) && (
                  <PlayerStatisticPanel
                    titleKey="assists"
                    players={mostAssistData}
                    shouldBlur={!pro}
                  />
                )}
                {mostMinutesData.length > 0 && (
                  <PlayerStatisticPanel
                    titleKey="mostMinutes"
                    players={mostMinutesData}
                  />
                )}
              </div>

              <LeagueStatisticsPlayer
                leagueId={league.id}
                onDataLoaded={handlePlayerStatsLoaded}
                isPremiumUser={isPremiumUser}
              />

              <div className="mb-4">
                <AdSlot
                  type={AdPositionEnum.LEAGUE_SCREEN_BANNER}
                  className="w-full"
                />
              </div>
            </div>
          </MainColumn>
          <RightRail className="gap-4">
            <StatisticsPanels
              key={`stats-${isPremiumUser ? "pro" : "free"}-${premiumUserKey}-${dataUpdateKey}`}
              playerStats={playerStatsData}
              isPremiumUser={isPremiumUser}
              hasRestriction={!pro}
            />
          </RightRail>
        </TwoColumnGrid>
      </TabletContainer>
    </TabletLayoutPage>
  );
};

export default LeaguePageWrapper;

type LeagueHeaderProps = {
  league: League | null;
  isCup: boolean;
  l: LanguageLocaleEnum;
  onBack: () => void;
  canFollow: boolean;
  isFollowing: boolean;
  onFollowToggle: () => void;
};

const LeagueHeader: React.FC<LeagueHeaderProps> = ({
                                                     league,
                                                     isCup,
                                                     l,
                                                     onBack,
                                                     canFollow,
                                                     isFollowing,
                                                     onFollowToggle
                                                   }) => (
  <RoundedRectangle
    className="league-header-card mb-4 bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pt-20"
    hasBorder
    hasTitle={false}
  >
    <BackButton onClick={onBack} />
    {league ? (
      <div className="flex items-center gap-4 px-6 xl:px-8 pb-6">
        <div className="flex flex-col">
                    <span className="text-base font-medium text-font dark:text-font-dark">
                        {isCup && league.subCategory ? showTranslated(league.subCategory.name, l) : showTranslated(league.name, l)}
                    </span>
          <span className="text-sm font-normal text-font-600 dark:text-font-600-dark">
                        {league.areasOfInterest?.map((aoiItem) => showTranslated(aoiItem.name, l)).join(" | ") || ""}
                    </span>
        </div>
        <div className="flex-1" />
        {canFollow && (
          <button
            type="button"
            onClick={onFollowToggle}
            className={`rounded-full h-9 py-1 px-3 flex justify-center items-center gap-1 transition-colors ${
              isFollowing
                ? "bg-primary-50 dark:bg-primary-50-dark text-font-300 dark:text-font-300-dark"
                : "bg-bg-50 dark:bg-bg-50-dark text-primary dark:text-primary"
            }`}
          >
            {translate(isFollowing ? "following" : "follow", l)}
          </button>
        )}
      </div>
    ) : (
      <span className="text-font-600 dark:text-font-600-dark px-6 xl:px-8 pb-6">
                {translate("loading", l)}
            </span>
    )}
  </RoundedRectangle>
);

type LeagueTableViewProps = {
  isVisible: boolean;
  isCup: boolean;
  leagueSelectorItems: { key: number; label: string }[];
  leagueId?: number;
  onLeagueChange: (value: number | string | undefined) => void;
  l: LanguageLocaleEnum;
};

const LeagueTableView: React.FC<LeagueTableViewProps> = ({
                                                           isVisible,
                                                           isCup,
                                                           leagueSelectorItems,
                                                           leagueId,
                                                           onLeagueChange,
                                                           l
                                                         }) => {
  if (!isVisible) return null;
  return (
    <div>
      {isCup && leagueSelectorItems.length > 1 && (
        <div className="mb-3 flex justify-start">
          <div className="w-auto min-w-64">
            <LeagueSelector
              value={leagueId}
              onChange={onLeagueChange}
              items={leagueSelectorItems}
              placeholder={translate("select", l)}
              transparent
              withoutChoose
              containerClassName="relative overflow-hidden h-9 flex-shrink-0 inline-flex items-center justify-start rounded-lg bg-white dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark pl-3 pr-8"
              className="text-left text-sm"
              dropdownMaxWidth={320}
              dropdownFitContent
            />
          </div>
        </div>
      )}
      {leagueId ? (
        <div className="rounded-xl mb-4  w-full">
          <Table team1Id={0} team2Id={0} leagueId={leagueId} />
        </div>
      ) : (
        <div className="p-4 text-font-600 dark:text-font-600-dark">
          {translate("loading", l)}
        </div>
      )}
    </div>
  );
};

